import PropTypes from 'prop-types';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import { URLS } from '@/constants/urls';
import { CallToActionKind, UntypedObject } from '@/types';
import styles from './TagList.module.scss';

type TagListProps = {
	t: (k: string) => string;
	children: React.ReactNode[];
	onDeleteAll?: (tag: UntypedObject) => void;
	displayScrollBar?: boolean;
	displayShowMore?: boolean;
	customClassName?: string;
};

export const TagList: React.FC<TagListProps> = ({
	t,
	children,
	onDeleteAll,
	displayScrollBar,
	displayShowMore,
	customClassName,
}: TagListProps) => {
	const [showAllTags, setShowAllTags] = useState(false);
	const [isOverflow, setIsOverflow] = useState(false);
	const [isSmall, setIsSmall] = useState(false);

	const count = React.Children.count(children);
	const tagsRef = useRef<HTMLDivElement | null>(null);

	const showMoreLabel = displayShowMore && t(`category:quickMenu.${showAllTags ? 'showLess' : 'showMore'}`);

	useEffect(() => {
		if (tagsRef?.current && tagsRef?.current?.parentNode?.parentElement) {
			setIsOverflow(tagsRef.current.clientWidth >= tagsRef.current.parentNode.parentElement.clientWidth);
		}
		setIsSmall(count > 0 && children?.every((child: ReactElement) => child?.props.isSmall));
	}, [count, tagsRef.current]);

	const onShowMore = () => setShowAllTags(!showAllTags);

	const tagsStyleName = [
		styles.tags,
		displayScrollBar ? styles['with-scrollbar'] : styles['without-scrollbar'],
		displayScrollBar && !displayShowMore && isOverflow ? styles['margin-bottom'] : '',
		displayShowMore ? (showAllTags ? styles['show-all-tags'] : styles['hide-all-tags']) : '',
		isSmall ? styles['small-tags'] : '',
		customClassName ? customClassName : '',
	]
		.filter(Boolean)
		.join(' ');

	return count > 0 ? (
		<div className={styles.container} id="tags-list" data-test-id="tag-list" data-rbf="tag-list">
			<div ref={tagsRef} className={tagsStyleName} data-test-id="children" data-rbf="children">
				{children}
				{onDeleteAll && count > 1 && (
					<CallToActionContainer
						kind={CallToActionKind.GHOST}
						className={styles['delete-all']}
						onClick={onDeleteAll}
						data-test-id="delete-all"
						data-rbf="delete-all"
					>
						{t('category:filter.deleteAll')}
					</CallToActionContainer>
				)}
			</div>
			{!displayScrollBar && displayShowMore && isOverflow && (
				<CallToActionContainer
					kind={CallToActionKind.TERTIARY}
					onClick={onShowMore}
					icon={{
						url: showAllTags ? URLS.ANGLE_TOP_BLACK : URLS.ANGLE_BOTTOM_BLACK,
						alt: showAllTags ? 'angle top' : 'angle bottom',
						rightSide: true,
					}}
					className={styles.btn}
				>
					{showMoreLabel}
				</CallToActionContainer>
			)}
		</div>
	) : null;
};

TagList.propTypes = {
	/** Translate function */
	// t: PropTypes.func,
	/** Children */
	// children: PropTypes.node.isRequired,
	/** onDeleteAll callback */
	onDeleteAll: PropTypes.func,
	/** displayScrollBar option */
	displayScrollBar: PropTypes.bool,
	/** displayShowMore option */
	displayShowMore: PropTypes.bool,
	customClassName: PropTypes.string,
};

export default TagList;
